<template>
    <div
        data-test-id="relay-info-pane"
        class="pane"
    >
        <div class="relay-header">
            <h2 data-test-id="relay-info-pane-relay-name">{{ relay.site_name }}</h2>
            <button
                data-test-id="relay-info-pane-close-button"
                @click="$emit('closed')"
            >
                <img
                    src="/images/close-white.svg"
                    alt="close"
                />
            </button>
        </div>

        <dl>
            <div>
                <dt>Position:</dt>
                <dd data-test-id="relay-info-pane-relay-location">{{ location }}</dd>
            </div>
            <div>
                <dt>Type:</dt>
                <dd data-test-id="relay-info-pane-relay-type">{{ relayType }}</dd>
            </div>
            <div>
                <dt>Last Updated:</dt>
                <dd data-test-id="relay-info-pane-relay-updated-at">{{ updatedAtLabel }}</dd>
            </div>
        </dl>
        <div class="radios-container">
            <div
                v-for="(radio, index) in radios"
                :key="index"
                class="radio-card"
            >
                <h4 :data-test-id="`relay-info-pane-relay-radio-${radio.id}-name`">{{ radio.name }}</h4>

                <div class="attribute-container">
                    <div class="attribute">
                        <span class="icon"
                            ><img
                                :alt="`Health: ${radioData[radio.id].health.label}`"
                                :data-test-id="`relay-info-pane-relay-radio-${radio.id}-health-icon`"
                                :src="radioData[radio.id].health.icon"
                        /></span>
                        <span
                            :data-test-id="`relay-info-pane-relay-radio-${radio.id}-health-label`"
                            class="value"
                            >{{ radioData[radio.id].health.label }}</span
                        >
                    </div>
                    <div class="attribute">
                        <span class="icon"
                            ><img
                                :alt="`Antenna Quality: ${radioData[radio.id].antenna.label}`"
                                :data-test-id="`relay-info-pane-relay-radio-${radio.id}-antenna-icon`"
                                :src="radioData[radio.id].antenna.icon"
                        /></span>
                        <span
                            :data-test-id="`relay-info-pane-relay-radio-${radio.id}-antenna-label`"
                            class="value"
                            >{{ radioData[radio.id].antenna.label }}</span
                        >
                    </div>
                    <div
                        v-if="!radioHasIntegratedPower(radio)"
                        class="attribute"
                    >
                        <span class="icon"
                            ><img
                                :alt="`Battery Level: ${radioData[radio.id].battery.label}`"
                                :data-test-id="`relay-info-pane-relay-radio-${radio.id}-battery-icon`"
                                :src="radioData[radio.id].battery.icon"
                        /></span>
                        <span
                            :data-test-id="`relay-info-pane-relay-radio-${radio.id}-battery-label`"
                            class="value"
                            >{{ radioData[radio.id].battery.label }}</span
                        >
                    </div>
                    <div class="attribute">
                        <span class="icon"
                            ><img
                                :alt="`Connection Status: ${radioData[radio.id].connection.label}`"
                                :data-test-id="`relay-info-pane-relay-radio-${radio.id}-connection-icon`"
                                :src="radioData[radio.id].connection.icon"
                        /></span>
                        <span
                            :data-test-id="`relay-info-pane-relay-radio-${radio.id}-connection-label`"
                            class="value"
                            >{{ radioData[radio.id].connection.label }}</span
                        >
                    </div>
                    <div class="attribute full-width">
                        <dl>
                            <div>
                                <dt class="value">Last Response:</dt>
                                <dd
                                    :data-test-id="`relay-info-pane-relay-radio-${radio.id}-last-response`"
                                    class="value"
                                >
                                    {{ radioData[radio.id].lastUpdated }}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
        </div>

        <div class="popup-footer">
            <router-link
                data-test-id="relay-info-pane-link-to-relay-details"
                :to="`/${organizationSlug}/relays/${relay.id}/details`"
                class="view icon-view"
                tag="a"
                target="_blank"
                title="View"
            />
        </div>
    </div>
</template>
<script>
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import relayTypes from '@/constants/relay-types'
import { getRadioScore, SCORE_COLOR_MINIMUMS } from '@/components/Map/utils/relayScore.js'

export default {
    components: {},

    mixins: [needsOrganizationSlug],

    props: {
        relay: {
            type: Object,
            required: true,
        },
    },

    emits: ['closed'],

    computed: {
        location() {
            const { latitude = null, longitude = null } = this.relay
            if (latitude || longitude) {
                return `${this.formatCoordinate(latitude)}, ${this.formatCoordinate(longitude)}`
            }

            return 'N/A'
        },

        relayType() {
            return !this.relay.type ? 'N/A' : relayTypes[this.relay.type]?.label
        },

        updatedAtLabel() {
            return this.formatDate(this.relay.updated_at)
        },

        radios() {
            return this.relay.assembly.members_with_asset
                .filter((asset) => asset.asset_type === 'asset_radio')
                .map((member) => ({
                    name: member.label,
                    ...member.asset,
                }))
        },

        radioData() {
            const radios = {}
            this.radios.forEach((radio) => {
                const { value: score } = getRadioScore(radio)
                const healthCheck = radio.latest_health_check ?? null
                const antennaQuality = healthCheck?.antenna_quality ?? null
                const batteryLevel = healthCheck?.battery_level ?? null
                const isConnected = healthCheck?.usb_connected ?? null
                const isCharging = healthCheck?.is_charging ?? null
                const lastResponseDate = healthCheck?.created_at ?? null

                radios[radio.id] = {
                    health: {
                        label: this.getRadioHealthLabel(score),
                        icon: this.getRadioHealthIcon(score),
                    },
                    antenna: {
                        icon: this.getAntennaIcon(antennaQuality),
                        label: this.getAntennaLabel(antennaQuality),
                    },
                    battery: {
                        icon: this.getBatteryIcon(batteryLevel),
                        label: this.getBatteryLabel(batteryLevel),
                    },
                    connection: {
                        icon: this.getConnectionIcon(isConnected, isCharging),
                        label: this.getConnectionLabel(isConnected, isCharging),
                    },
                    lastUpdated: this.formatDate(lastResponseDate),
                }
            })

            return radios
        },
    },

    methods: {
        buildAntennaIcon(value) {
            return `/images/relays/map/info-pane/antenna-${value}.svg`
        },
        buildBatteryIcon(value) {
            return `/images/relays/map/info-pane/battery-${value}.svg`
        },
        buildConnectionIcon(value) {
            return `/images/relays/map/info-pane/power-${value}.svg`
        },
        buildHealthIcon(value) {
            return `/images/relays/map/info-pane/health-${value}.svg`
        },

        getRadioHealthLabel(score) {
            if (score >= SCORE_COLOR_MINIMUMS.GREEN) {
                return 'Healthy'
            }

            if (score >= SCORE_COLOR_MINIMUMS.YELLOW) {
                return 'Degraded'
            }

            if (score >= SCORE_COLOR_MINIMUMS.RED) {
                return 'Poor'
            }

            return 'Unknown'
        },

        getRadioHealthIcon(score) {
            if (score >= SCORE_COLOR_MINIMUMS.GREEN) {
                return this.buildHealthIcon('good')
            }

            if (score >= SCORE_COLOR_MINIMUMS.YELLOW) {
                return this.buildHealthIcon('degraded')
            }

            if (score >= SCORE_COLOR_MINIMUMS.RED) {
                return this.buildHealthIcon('poor')
            }

            return this.buildHealthIcon('unknown')
        },

        getAntennaLabel(antennaQuality) {
            return antennaQuality?.toUpperCase() ?? 'Unknown'
        },

        getAntennaIcon(antennaQuality) {
            const normalizedValue = antennaQuality === null ? null : antennaQuality.toUpperCase()

            switch (normalizedValue) {
                case 'EXCELLENT':
                case 'GOOD':
                    return this.buildAntennaIcon('good')
                    // eslint-disable-next-line no-unreachable
                    break
                case 'AVERAGE':
                    return this.buildAntennaIcon('degraded')
                    // eslint-disable-next-line no-unreachable
                    break
                case 'POOR':
                case 'BAD':
                    return this.buildAntennaIcon('poor')
                    // eslint-disable-next-line no-unreachable
                    break
                default:
                    return this.buildAntennaIcon('unknown')
                    // eslint-disable-next-line no-unreachable
                    break
            }
        },

        getBatteryIcon(batteryLevel) {
            if (batteryLevel === null) {
                return this.buildBatteryIcon('unknown')
            }

            if (batteryLevel === 100) {
                return this.buildBatteryIcon('100-percent')
            }

            if (batteryLevel >= 75) {
                return this.buildBatteryIcon('80-percent')
            }

            if (batteryLevel >= 26) {
                return this.buildBatteryIcon('50-percent')
            }

            if (batteryLevel >= 11) {
                return this.buildBatteryIcon('20-percent')
            }

            return this.buildBatteryIcon('0-percent')
        },

        getBatteryLabel(batteryLevel) {
            return batteryLevel === null ? 'Unknown' : `${batteryLevel}%`
        },

        getConnectionIcon(isConnected, isCharging) {
            if (isConnected === null || isCharging === null) {
                return this.buildConnectionIcon('unknown')
            }

            if (!isConnected) {
                return this.buildConnectionIcon('not-charging')
            }

            return this.buildConnectionIcon('connected')
        },

        getConnectionLabel(isConnected, isCharging) {
            if (isConnected === null || isCharging === null) {
                return 'Unknown'
            }

            if (!isConnected) {
                return 'Not Charging'
            }

            if (isCharging) {
                return 'Charging'
            }

            return 'Connected to Power'
        },

        formatDate(date) {
            return !date ? 'N/A' : this.$filters.whenFromNow(date)
        },
        formatCoordinate(coordinate) {
            return Intl.NumberFormat('en-US', {
                maximumFractionDigits: 8,
                useGrouping: false,
            }).format(coordinate)
        },
        radioHasIntegratedPower(radio) {
            return radio.serial_number.startsWith('MNE')
        },
    },
}
</script>
<style lang="scss">
.pane {
    border-radius: 8px;
    background-color: #2b2930;
    color: #f1f1f1;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);

    margin: 0;
    width: 300px;
    line-height: 1.5;
    padding: 8px 12px;

    .relay-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 5px 0 8px 0;

        h2 {
            margin: 0;
            font-size: 16px;
            font-family: 'Avenir Heavy', sans-serif;
            font-weight: 500;
        }

        button {
            background: none;
            border: none;
            cursor: pointer;
        }
    }

    dl {
        display: flex;
        gap: 6px;
        flex-direction: column;
        font-size: 12px;

        div {
            display: flex;
            gap: 1ex;

            dt {
                font-weight: 700;
                display: inline;
                font-family: 'Avenir Heavy', sans-serif;
            }

            dd {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: inline;
                font-family: 'Avenir Light', sans-serif;
            }
        }
    }

    .radios-container {
        display: flex;
        flex-direction: column;

        .radio-card {
            padding-top: 10px;
            margin-top: 10px;
            border-top: 1px solid #49454f;

            h4 {
                font-size: 14px;
                font-family: 'Avenir Heavy', sans-serif;
                font-weight: 600;
                color: #bec3dc;
                text-decoration: underline;
            }

            .attribute-container {
                display: grid;
                /* Create two equal columns */
                grid-template-columns: 1fr 1fr;
                /* Add spacing between rows and columns */
                gap: 4px 20px;
                /* Optional: Indent slightly from the header */
                padding-top: 4px;
                font-family: 'Avenir Light', sans-serif;
                font-size: 12px;

                .attribute {
                    display: flex;
                    align-items: center;
                    color: #555;

                    .icon {
                        margin-right: 8px;
                        flex-shrink: 0;
                    }

                    .value {
                        color: #ffffff;
                        font-weight: 400;
                        font-family: 'Avenir Light', sans-serif;
                    }

                    &.full-width {
                        grid-column: 1 / -1;
                    }

                    dt {
                        font-weight: 400;
                        display: inline;
                        font-family: 'Avenir Light', sans-serif;
                    }
                }
            }
        }
    }

    .popup-footer {
        margin-top: 10px;
        padding-top: 15px;
        padding-bottom: 15px;
        border-top: 1px solid #49454f;

        .icon-view {
            border: 1px solid #888;
            border-radius: 50%;
            padding: 5px;
            text-decoration: none;
            font-size: 1rem;
            color: #ddd;
            vertical-align: middle;

            &:before {
                color: #ddd;
            }
        }
    }
}
</style>
